import { toast } from '@/components/UI/Toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  PlaygroundModeStatus,
  turnPlaygroundModeOff,
  turnPlaygroundModeOn
} from '../../../http/Company/playgroundModeApi';
import { USER_NOT_SIGNED_IN_ERROR_MESSAGE } from '../../../http/Helpers/acquireAccessToken';
import queryKeys from '../queries/queryKeys';

/**
 * Toggles QA Playground Mode on/off for the caller's own session (Feature #8075, PBI #8080).
 * On success, writes the new state directly into the playgroundMode query cache so the
 * banner and toggle reflect it immediately (AC3), rather than waiting on a refetch. On
 * failure, the cache is deliberately left untouched — the switch (a controlled component
 * bound to the cached `isActive`) snaps back to the last confirmed state on its own, and a
 * toast tells the user why, instead of silently swallowing the rejection (PR #2577 review,
 * Koketso Ramokhubathi — this only surfaces at all once the underlying API helper actually
 * re-throws on failure; see httpRequestHelpers.ts).
 *
 * The `retry` option (Bug #8516) covers one more layer of the same MSAL-settling race
 * acquireAccessToken's own bounded wait already covers: if that wait's retries are themselves
 * exhausted, acquireAccessToken throws its specific USER_NOT_SIGNED_IN_ERROR_MESSAGE Error, and
 * unlike usePlaygroundMode's query (which gets 2 retries by default), a mutation defaults to 0 —
 * so this one attempt became a final, user-facing toast with no second chance. Retrying is scoped
 * to exactly that error (imported from acquireAccessToken.ts rather than duplicated as a string
 * literal — auto-review finding, fp:retry-matches-literal-error-message, PR #2611: a hand-copied
 * literal silently stops matching the moment either side's wording changes, with no test failing
 * on the string alone), not failures in general: a real server error (e.g. a 500) must keep
 * surfacing immediately, as every test below except the new race-condition one asserts.
 */
const usePlaygroundModeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (isActive: boolean) => {
      if (isActive) {
        await turnPlaygroundModeOn();
      } else {
        await turnPlaygroundModeOff();
      }
      return isActive;
    },
    retry: (failureCount, error) =>
      failureCount < 2 &&
      error instanceof Error &&
      error.message === USER_NOT_SIGNED_IN_ERROR_MESSAGE,
    onSuccess: (isActive) => {
      queryClient.setQueryData<PlaygroundModeStatus>([queryKeys.playgroundMode], {
        isActive
      });
    },
    onError: (error) => {
      console.error('Playground Mode toggle failed:', error);
      toast({
        title: 'Could not change Playground Mode',
        description: 'Please try again.',
        color: 'danger'
      });
    }
  });
};

export default usePlaygroundModeMutation;
